import { Routes } from '@angular/router';
import { authGuard } from './core/auth/auth.guard';
import { subscriptionGuard } from './core/auth/subscription.guard';
import { apiKeysGuard } from './core/auth/api-keys.guard';

export const routes: Routes = [
  // ── PUBLIC — no guards ────────────────────────────────────────────────────
  { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
  {
    path: 'approve',
    loadComponent: () =>
      import('./features/approve/approve.component').then((m) => m.ApproveComponent),
    title: 'Approve Proposal — SellerSage',
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./features/auth/login.component').then((m) => m.LoginComponent),
    title: 'Login — SellerSage',
  },
  {
    path: 'register',
    loadComponent: () =>
      import('./features/auth/register.component').then((m) => m.RegisterComponent),
    title: 'Register — SellerSage',
  },
  {
    path: 'set-password',
    loadComponent: () =>
      import('./features/auth/set-password.component').then((m) => m.SetPasswordComponent),
    title: 'Set Password — SellerSage',
  },
  {
    path: 'unsubscribe',
    loadComponent: () =>
      import('./features/unsubscribe/unsubscribe.component').then((m) => m.UnsubscribeComponent),
    title: 'Email Preferences — SellerSage',
  },

  // ── ONBOARDING — authGuard only, standalone pages (no Shell/sidebar) ──────
  {
    path: 'onboarding',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/onboarding/onboarding.component').then(
        (m) => m.OnboardingComponent,
      ),
    title: 'Set Up Notifications — SellerSage',
  },
  {
    path: 'credentials/setup',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/credentials/credential-setup.component').then(
        (m) => m.CredentialSetupComponent,
      ),
    title: 'Connect Your APIs — SellerSage',
  },

  {
    path: 'marketplace-picker',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/marketplace-picker/marketplace-picker.component').then(
        (m) => m.MarketplacePickerComponent,
      ),
    title: 'Select Marketplaces — SellerSage',
  },

  // ── SHELL — Billing (authGuard only, no apiKeysGuard needed) ─────────────
  {
    path: 'billing',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./shared/components/layout/shell.component').then((m) => m.ShellComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/billing/billing.component').then((m) => m.BillingComponent),
        title: 'Billing & Subscription — SellerSage',
      },
      {
        path: 'subscribe',
        loadComponent: () =>
          import('./features/billing/billing.component').then((m) => m.BillingComponent),
        title: 'Subscribe — SellerSage',
      },
    ],
  },

  // ── PROTECTED SHELL — authGuard + subscriptionGuard + apiKeysGuard ──────────
  {
    path: '',
    canActivate: [authGuard, subscriptionGuard, apiKeysGuard],
    loadComponent: () =>
      import('./shared/components/layout/shell.component').then((m) => m.ShellComponent),
    children: [
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/dashboard/dashboard.component').then((m) => m.DashboardComponent),
        title: 'Dashboard',
      },
      {
        path: 'proposals/:id',
        loadComponent: () =>
          import('./features/proposals/proposal-detail.component').then(
            (m) => m.ProposalDetailComponent,
          ),
        title: 'Proposal Detail — SellerSage',
      },
      {
        path: 'proposals',
        loadComponent: () =>
          import('./features/proposals/proposals.component').then((m) => m.ProposalsComponent),
        title: 'AI Proposals',
        pathMatch: 'full',
      },
      {
        path: 'fbm-orders',
        loadComponent: () =>
          import('./features/fbm-orders/fbm-orders.component').then((m) => m.FbmOrdersComponent),
        title: 'FBM Orders',
      },
      {
        path: 'alerts',
        loadComponent: () =>
          import('./features/alerts/alerts.component').then((m) => m.AlertsComponent),
        title: 'Alerts',
      },
      {
        path: 'metrics',
        loadComponent: () =>
          import('./features/metrics/metrics.component').then((m) => m.MetricsComponent),
        title: 'Metrics',
      },
      {
        path: 'reports',
        loadComponent: () =>
          import('./features/reports/reports.component').then((m) => m.ReportsComponent),
        title: 'Reports',
      },
      {
        path: 'analytics',
        loadComponent: () =>
          import('./features/analytics/analytics.component').then((m) => m.AnalyticsComponent),
        title: 'Analytics',
      },
      {
        path: 'credentials',
        loadComponent: () =>
          import('./features/credentials/credentials.component').then(
            (m) => m.CredentialsComponent,
          ),
        title: 'API Credentials',
      },
      {
        path: 'cogs',
        loadComponent: () =>
          import('./features/cogs/cogs.component').then((m) => m.CogsComponent),
        title: 'COGS Manager',
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./features/settings/settings.component').then((m) => m.SettingsComponent),
        title: 'Settings',
      },
      {
        path: 'reimbursement',
        loadComponent: () =>
          import('./features/reimbursement/reimbursement.component').then(
            (m) => m.ReimbursementComponent,
          ),
        title: 'Reimbursement Radar',
      },
      {
        path: 'review-requester',
        loadComponent: () =>
          import('./features/review-requester/review-requester.component').then(
            (m) => m.ReviewRequesterComponent,
          ),
        title: 'Auto Review Requester — SellerSage',
      },
      {
        path: 'review-insights',
        loadComponent: () =>
          import('./features/review-insights/review-insights.component').then(
            (m) => m.ReviewInsightsComponent,
          ),
        title: 'Review Insights — SellerSage',
      },
      {
        path: 'listing-readiness',
        loadComponent: () =>
          import('./features/listing-readiness/listing-readiness.component').then(
            (m) => m.ListingReadinessComponent,
          ),
        title: 'Listing Readiness — SellerSage',
      },
      // Brand Analytics (G2 / P2 #16) — one route family for both sub-views so a
      // deep link lands on the right one and the shell nav row stays active for
      // both. SQP ships first (Slice 1); Marketplace Search Terms joins in Slice 2.
      {
        path: 'brand-analytics',
        children: [
          { path: '', redirectTo: 'sqp', pathMatch: 'full' },
          {
            path: 'sqp',
            loadComponent: () =>
              import('./features/brand-analytics/sqp.component').then((m) => m.SqpComponent),
            title: 'Search Query Performance — SellerSage',
          },
          {
            path: 'search-terms',
            loadComponent: () =>
              import('./features/brand-analytics/search-terms.component').then(
                (m) => m.BrandAnalyticsSearchTermsComponent,
              ),
            title: 'Marketplace Search Terms — SellerSage',
          },
        ],
      },
      {
        path: 'campaigns',
        loadComponent: () =>
          import('./features/campaigns/campaigns.component').then((m) => m.CampaignsComponent),
        title: 'Campaigns',
      },
      {
        path: 'search-terms',
        loadComponent: () =>
          import('./features/search-terms/search-terms.component').then(
            (m) => m.SearchTermsComponent,
          ),
        title: 'Search Terms — SellerSage',
      },
      // Search term trends (G5 / P2 #19) — the seller's OWN Sponsored-Products
      // search-term impression-share + rank trends over time. Growth+/Pro (the
      // component + backend gate below Growth; nav item locks via SEARCH_TERM_TRENDS).
      {
        path: 'search-term-trends',
        loadComponent: () =>
          import('./features/search-term-trends/search-term-trends.component').then(
            (m) => m.SearchTermTrendsComponent,
          ),
        title: 'Search Term Trends — SellerSage',
      },
      {
        path: 'inventory',
        loadComponent: () =>
          import('./features/inventory/inventory.component').then((m) => m.InventoryComponent),
        title: 'Inventory',
      },
      {
        path: 'inventory/:asin/:marketplaceId',
        loadComponent: () =>
          import('./features/product-detail/product-detail.component').then(
            (m) => m.ProductDetailComponent,
          ),
        title: 'Product Detail',
      },
    ],
  },
  // ── CONNECT STORE — authGuard only, standalone page ───────────────
  {
    path: 'connect-store',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/connect-store/connect-store.component').then(
        (m) => m.ConnectStoreComponent,
      ),
    title: 'Connect Your Store — SellerSage',
  },

  // ── MOBILE BATCH — no auth guard, JWT authenticated via query param ─
  {
    path: 'mobile/batch',
    loadComponent: () =>
      import('./features/mobile-batch/mobile-batch.component').then(
        (m) => m.MobileBatchComponent,
      ),
    title: 'Batch Fulfillment — SellerSage',
  },

  { path: '**', redirectTo: 'dashboard' },
];
